import { useEffect } from 'react';
import useSwr from 'swr';
import toast from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import { formatDistance } from 'date-fns';

import { listAlertsByUser } from '@sdk/managers/AlertManager';
import { getPollDetail } from '@sdk/managers/PollsManager';
import { useAuth } from '@hooks/Auth';

import { ChevronLeftIcon } from '@components/elements/Icons';
import {
  AlertDetail,
  AlertMessageTemplate,
  AlertTitleTemplate,
} from '@utils/Alerts';
import { AlertType } from '@sdk/API';

const getAlertsByUser = async (
  _query: string,
  userId: string
): Promise<AlertDetail[]> => {
  if (!userId) throw new Error('You are not logged in.');
  const res: any = await listAlertsByUser(userId, 1000, null);

  const data = res.data.result;

  return Promise.all(
    data.items.map(async (alert) => {
      if (!alert.pollID) return alert;

      try {
        const pollDataRes: any = await getPollDetail(
          ['title', 'owner', 'image { key bucket region }'],
          alert.pollID
        );
        return {
          ...alert,
          poll: pollDataRes.data.result,
        };
      } catch (err: any) {
        console.error(err);
        return alert;
      }
    })
  );
};

const AlertRouter: React.FC = () => {
  const history = useHistory<any>();
  const { dbUser } = useAuth();

  const { data, error } = useSwr<AlertDetail[]>(
    dbUser?.id ? ['/alerts', dbUser.id] : null,
    getAlertsByUser
  );

  console.log({ data, error });

  useEffect(() => {
    if (error) {
      console.log(error);
      toast.error(
        (error.errors?.length || 0) > 0
          ? error.errors?.[0]?.message
          : error.message
      );
    }
  }, [error]);

  return (
    <div className="min-h-screen x-pattern-yellow">
      <div className="container mx-auto">
        <div className="px-4 pt-12 pb-2">
          <h1 className="text-3xl text-black ">Your Alerts</h1>
        </div>

        {data && (
          <div className="pb-32 mx-4 space-y-4">
            {data?.map((alert) => {
              let title = alert.type === 'EVENT' ? alert.title : AlertTitleTemplate[alert.type!];
              let message = alert.type === 'EVENT' ? alert.message : AlertMessageTemplate[alert.type!];
              if (alert.type === AlertType.PROMOTION && alert.message)
                message = alert.message;

              if (alert.poll?.owner) {
                title = title && title.replaceAll('{owner}', alert.poll.owner);
                message = message && message.replaceAll('{owner}', alert.poll.owner);
              }
              let linkPoll = true;
              if (
                (alert.type as AlertType) === AlertType.POLL_VOTE_CLOSED ||
                (alert.type as AlertType) === AlertType.POLL_PLEDGING_CLOSED
              ) {
                linkPoll = false;
              }

              return (
                <div
                  key={alert.id}
                  role={linkPoll && alert.pollID ? 'button' : 'listitem'}
                  className="flex items-center justify-between p-4 transition bg-white border-3 border-orange hover:bg-orange"
                  onClick={
                    alert.pollID && linkPoll
                      ? () => history.push(`/poll/${alert.pollID}`)
                      : undefined
                  }
                >
                  <div className="flex-1">
                    <div className="flex justify-between w-full">
                      <div className="text-xs font-bold break-all font-display text-purple">
                        {title}
                      </div>
                      {alert.createdAt && (
                        <div className="text-xs text-gray-400 font-display">
                          {formatDistance(
                            Date.parse(alert.createdAt),
                            new Date()
                          )}{' '}
                          ago
                        </div>
                      )}
                    </div>
                    <h2 className="text-lg">{alert.poll?.title}</h2>
                    <div className="text-sm">{message}</div>
                  </div>
                  <div>
                    <ChevronLeftIcon className="transform rotate-180" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertRouter;
